
import React from 'react';
import { Plus, Users } from 'lucide-react';

interface SecondaryButtonProps {
  text: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'default' | 'danger' | 'warning';
  loading?: boolean;
}

interface ModernSettingHeaderProps {
  title: string;
  description?: string;
  buttonText?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  stats?: Array<{ label: string; value: string | number; }>;
  className?: string;
  children?: React.ReactNode;
  secondaryButton?: SecondaryButtonProps;
  actionSlot?: React.ReactNode;
}

const ModernSettingHeader: React.FC<ModernSettingHeaderProps> = ({
  title,
  description,
  buttonText,
  onClick,
  icon = <Users className="w-6 h-6" />,
  stats,
  className = '',
  children,
  secondaryButton,
  actionSlot,
}) => {
  return (
    <div className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 ${className}`}>
      {/* Main Header */}
      <div className="flex items-center justify-between gap-4">
        {/* Left Content */}
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              {icon}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {description}
            </p>
          </div>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Action Slot (e.g. search input) */}
          {actionSlot}

          {/* Secondary Button */}
          {secondaryButton && (
            <button
              onClick={secondaryButton.onClick}
              disabled={secondaryButton.loading}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                secondaryButton.variant === 'danger'
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 focus:ring-red-500'
                  : secondaryButton.variant === 'warning'
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 focus:ring-amber-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500'
              }`}
            >
              {secondaryButton.loading ? (
                <svg className="animate-spin w-4 h-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : secondaryButton.icon ? (
                <span className="mr-1.5">{secondaryButton.icon}</span>
              ) : null}
              {secondaryButton.text}
            </button>
          )}

          {/* Primary Button */}
          {(onClick && buttonText) && (
            <button
              onClick={onClick}
              className="inline-flex items-center px-3 py-1.5 bg-brandColor hover:opacity-90 text-white text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {buttonText}
            </button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      {stats && stats.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center lg:text-left">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Additional Content */}
      {children && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

export default ModernSettingHeader;